import { useMutation } from '@tanstack/react-query';
import { useTolgee } from '@tolgee/react';
import mixpanel from 'mixpanel-browser';
import { useCallback } from 'react';

import backendService from '../services/backendService';
import useAccessBadge from './useAccessBadge';
import {
  getBrowserLanguage,
  resolveLocaleFromServerSelection,
  storePreferredLocale,
} from './useLocale';

/**
 * The single place that knows how to change the signed-in user's language.
 *
 * It is a five-step sequence and the order matters: resolve the locale
 * server-side (which also sets the language cookie), write it to the user's
 * profile, refetch the access badge, mirror it into localStorage, and only then
 * tell tolgee.
 *
 * The profile write is the step that is easy to miss and expensive to get wrong.
 * `useLocale` resolves the active language as
 * `accessBadge.user.preferredLocale ?? getPreferredLocale()` — the badge wins over
 * localStorage — so a control that changes tolgee and localStorage but not the
 * profile appears to work and then silently reverts on the next page load. That
 * happened once already, which is why this lives in one place: any new language
 * control should call this rather than reimplementing the sequence.
 *
 * Anonymous visitors have no profile to write to, so they get the cookie +
 * localStorage + tolgee path only.
 */
function useChangeLocale({ onSelected }: { onSelected?: (locale: string) => void } = {}) {
  const tolgee = useTolgee();
  const { accessBadge, refetchAccessBadge } = useAccessBadge();

  const { mutate, isPending } = useMutation({
    mutationFn: ({ locale }: { locale: string }) =>
      backendService.instance.users.editProfile({ preferredLocale: locale }),
    onSuccess: (result, { locale }) => {
      refetchAccessBadge();
      storePreferredLocale(result.preferredLocale ?? locale);
    },
  });

  const changeLocale = useCallback(
    async (locale: string) => {
      const resolvedLocale = await resolveLocaleFromServerSelection(locale);
      if (accessBadge?.user?.id) {
        mutate({ locale: resolvedLocale }, { onSuccess: () => onSelected?.(locale) });
      } else {
        onSelected?.(locale);
      }
      storePreferredLocale(resolvedLocale);
      tolgee.changeLanguage(resolvedLocale);
      mixpanel.track('locale changed', {
        selectedLocale: locale,
        resolvedLocale,
        detectedLocale: getBrowserLanguage(),
      });
    },
    [accessBadge, mutate, tolgee, onSelected],
  );

  return { changeLocale, isChangingLocale: isPending };
}

export default useChangeLocale;
